
import { FileNodeFactory } from "./Nodes/FileNodeFactory.js";
import { generateZipFrom } from "./Zip.js";

import { Report } from "./Reports/Report.js";

/* ==----------------------------------------------------------------------------------------------------------== */
/* ==-----------------------------------------== Global functions ==-------------------------------------------== */
/* ==----------------------------------------------------------------------------------------------------------== */

/**
 * @description Handles retrieving data depending on the type of FileNode
 * @param { [FileNode] } files
 * @param { Int } max maximum simultaneous downloads allowed; zero or negative value means infinite
 * @returns { Report } Report containing status codes
 */
export async function downloadRequiredFiles(files, max = -1) {
    let downloadableFiles = files.filter(file => file.handlesDownloads());

    /* Zero / negative values defaults to full size */
    if (max <= 0) {
        max = files.length;
    }

    let chunks = [];

    /* Split the download files into chunks */
    for (let i = 0; i < downloadableFiles.length; i += max) {
        let chunk = downloadableFiles.slice(i, i + max);

        chunks.push(chunk);
    }

    /* Go over every chunk and download them in parallel */
    for (let chunk of chunks) {
        let promises = [];

        /* Download */
        chunk.forEach(file => {
            let promise = file.download();
            promises.push(promise);
        })

        /* Await */
        let report = await Promise.all(promises).then(reports => {
            let failedReport = reports.find(report => !report.isSuccess());

            if (failedReport !== undefined) {
                return failedReport;
            }

            return Report.ok();
        });

        if (!report.isSuccess()) {
            return report;
        }
    }

    return Report.ok();
}

/**
 * @description Convert projectData.instances into usable FileNode objects
 * @param { [ProjectData.Instances] } instances
 * @returns { [FileNode] }
 */
export async function convertInstancesToNodes(instances) {
    let files = [];
    let filesSet = new Set();

    let uniqueInstances = instances.filter(instance => {

        if (filesSet.has(instance.publicWebUrl) || !instance.publicWebUrl) {
            return false;
        }
        filesSet.add(instance.publicWebUrl);
        return true;
    });

    for (let instance of uniqueInstances) {
        let file = FileNodeFactory.createFromInstance(instance);
            files.push(file);    
        
    }

    return files;
}

/**
 * @description Downloads a list of instances and archives them
 * @param { [ProjectData.Instances] } instances
 * @param { String } archiveName
 * @returns { Report } The download exit status
 */
export async function downloadInstances(instances, linkedInstances, archiveName = "archive.zip") {

    let instancesToDownload = [...instances];
   
    if (linkedInstances && linkedInstances.length > 0) {
        linkedInstances.forEach((linkedInstance) => {
            instancesToDownload.push(linkedInstance)
        })
    }
    let files = await convertInstancesToNodes(instancesToDownload);

    let downloadReport = await downloadRequiredFiles(files, 4)

    /* Major download error */
    if (!downloadReport.isSuccess()) {
        downloadReport.data = instances.length; // For notification message
        return downloadReport;
    }

    const namedFiles = files.filter(file => file.name !== null);

    let zipReport = await generateZipFrom(archiveName, namedFiles);

    /* Zip is invalid */
    if (!zipReport.isSuccess()) {
        zipReport.data = instances.length;      // For notification message
        return zipReport;
    }

    let saveReport = zipReport.data.save();

    /* The zip could not be saved */
    if (!saveReport.isSuccess()) {
        saveReport.data = instances.length;      // For notification message
        return saveReport;
    }

    return Report.data(instances.length);
}
